import client from '@/graphql/client'
import GetProject from '@/graphql/queries/GetProject'

// Active-project + project-cache store. Historically this was backed by the per-project
// TeamMember table (listProjectMembershipsForCurrentUser + TeamMember subscriptions) and
// also stored per-user workflow ordering / last-access on the TeamMember row. Access is now
// org-membership only, so the active project is resolved org-natively via getProject (the
// membership-gated resolver) and held in `activeProject`. The per-user workflow drag-ordering
// and last-access features were dropped — they only ever worked for legacy TeamMember rows,
// which org users never had.
//
// `projectMemberships` is the legacy "my projects" list (items shaped `{ id, project }`,
// id === project.id); it is kept in sync by create/update/delete so existing consumers keep
// working, and is intentionally separate from `activeProject` so the minimal getProject shape
// never leaks into the richer list consumers.
export const projectMemberships = {
  namespaced: true,
  state: {
    projectMemberships: null,
    activeProject: null,
    activeProjectId: null
  },
  mutations: {
    addProjectMembership (state, payload) {
      const project = payload?.project || payload
      if (!project?.id) return
      if (!state.projectMemberships) state.projectMemberships = []
      const index = state.projectMemberships.findIndex(membership => membership.project?.id === project.id)
      if (index === -1) {
        state.projectMemberships.unshift({ id: project.id, project })
      } else {
        state.projectMemberships[index] = { id: project.id, project }
      }
    },
    deleteProjectMembership (state, payload) {
      if (state.projectMemberships) {
        state.projectMemberships = state.projectMemberships.filter(membership => membership.project?.id !== payload.projectId)
      }
      if (state.activeProject?.id === payload.projectId) {
        state.activeProject = null
      }
    },
    updateProject (state, payload) {
      if (state.projectMemberships) {
        const index = state.projectMemberships.findIndex(membership => membership.project?.id === payload.id)
        if (index >= 0) {
          state.projectMemberships[index].project = { ...state.projectMemberships[index].project, ...payload }
        }
      }
      if (state.activeProject?.id === payload.id) {
        state.activeProject = { ...state.activeProject, ...payload }
      }
    },
    setActiveProjectObject (state, payload) {
      state.activeProject = payload
    },
    setActiveProjectId (state, payload) {
      state.activeProjectId = payload
    },
    updateProjectOwner (state, payload) {
      const apply = (project) => ({ ...project, organisation: payload.organisation, organisationId: payload.organisation.id })
      if (state.projectMemberships) {
        const index = state.projectMemberships.findIndex(membership => membership.project?.id === payload.id)
        if (index >= 0) {
          state.projectMemberships[index].project = apply(state.projectMemberships[index].project)
        }
      }
      if (state.activeProject?.id === payload.id) {
        state.activeProject = apply(state.activeProject)
      }
    }
  },
  actions: {
    // Resolve and cache the active project via the org-membership-gated getProject resolver,
    // so org users get a populated project context (name + ownership) with no TeamMember row.
    async setActiveProject ({ commit }, { projectId }) {
      commit('setActiveProjectId', projectId)
      if (!projectId) {
        commit('setActiveProjectObject', null)
        return
      }
      try {
        const { getProject } = await client.query({
          operationName: 'getProject',
          query: GetProject,
          variables: { projectId }
        })
        commit('setActiveProjectObject', getProject || null)
      } catch (e) {
        console.error('setActiveProject: getProject failed', e)
      }
    }
  },
  getters: {
    activeProject: (state) => state.activeProject,
    myProjects (state) {
      return (state.projectMemberships || []).map(membership => membership.project)
    }
  }
}
